// Command Palette - Modal de busca estilo moderno
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, User, Package, X, ArrowUp, ArrowDown } from 'lucide-react';
import { searchNotasFiscais } from '../../services/notasFiscaisService';
import { searchClientes } from '../../services/clientesService';
import { searchProdutos } from '../../services/produtosService';
import { useEmpresa } from '../../contexts/EmpresaContext';
import { formatCurrency } from '../../utils/formatters';
import { cn } from '../../utils/cn';

interface SearchResult {
  id: string;
  type: 'nota' | 'cliente' | 'produto';
  title: string;
  subtitle: string;
  data: any;
}

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const { empresaSelecionada } = useEmpresa();
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Focus no input quando abrir
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } else {
      setSearchTerm('');
      setResults([]);
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Buscar resultados quando o termo de busca mudar
  useEffect(() => {
    if (searchTerm.trim().length < 2) {
      setResults([]);
      return;
    }

    if (!empresaSelecionada) {
      setResults([]);
      return;
    }

    const buscarResultados = async () => {
      try {
        setLoading(true);
        const term = searchTerm.trim();
        const searchResults: SearchResult[] = [];

        // Buscar notas fiscais
        try {
          const notas = await searchNotasFiscais(term, empresaSelecionada.id);
          notas.slice(0, 5).forEach(nota => {
            searchResults.push({
              id: nota.id,
              type: 'nota',
              title: `Nota ${nota.numero}/${nota.serie} - ${nota.tipo}`,
              subtitle: `${nota.destinatario.nome} - ${formatCurrency(nota.total)}`,
              data: nota,
            });
          });
        } catch (err) {
          console.error('Erro ao buscar notas:', err);
        }

        // Buscar clientes
        try {
          const clientes = await searchClientes(term, empresaSelecionada.id);
          clientes.slice(0, 3).forEach(cliente => {
            searchResults.push({
              id: cliente.id,
              type: 'cliente',
              title: cliente.nome,
              subtitle: cliente.tipo === 'F' ? cliente.cpf || '' : cliente.cnpj || '',
              data: cliente,
            });
          });
        } catch (err) {
          console.error('Erro ao buscar clientes:', err);
        }

        // Buscar produtos
        try {
          const produtos = await searchProdutos(term, empresaSelecionada.id);
          produtos.slice(0, 2).forEach(produto => {
            searchResults.push({
              id: produto.id,
              type: 'produto',
              title: produto.descricao,
              subtitle: `${produto.codigo} - ${formatCurrency(produto.precoVenda)}`,
              data: produto,
            });
          });
        } catch (err) {
          console.error('Erro ao buscar produtos:', err);
        }

        // Limitar a 10 resultados
        setResults(searchResults.slice(0, 10));
        setSelectedIndex(0);
      } catch (err) {
        console.error('Erro na busca:', err);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(buscarResultados, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, empresaSelecionada]);

  // Navegação por teclado
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
      } else if (event.key === 'Enter' && results[selectedIndex]) {
        event.preventDefault();
        handleResultClick(results[selectedIndex]);
      } else if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex, onClose]);

  // Scroll para item selecionado
  useEffect(() => {
    if (resultsRef.current && selectedIndex >= 0) {
      const selectedElement = resultsRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex]);

  const handleResultClick = (result: SearchResult) => {
    setSearchTerm('');
    setResults([]);
    onClose();

    if (result.type === 'nota') {
      navigate(`/app/notas/${result.id}`);
    } else if (result.type === 'cliente') {
      navigate(`/app/cadastros/clientes`);
    } else if (result.type === 'produto') {
      navigate(`/app/cadastros/produtos`);
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'nota':
        return <FileText className="w-4 h-4" />;
      case 'cliente':
        return <User className="w-4 h-4" />;
      case 'produto':
        return <Package className="w-4 h-4" />;
      default:
        return <Search className="w-4 h-4" />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'nota':
        return 'Nota Fiscal';
      case 'cliente':
        return 'Cliente';
      case 'produto':
        return 'Produto';
      default:
        return '';
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center pt-[20vh] p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md animate-fade-in" />

      {/* Modal */}
      <div
        className={cn(
          'relative z-10 w-full max-w-2xl bg-white dark:bg-gray-900 backdrop-blur-sm',
          'rounded-2xl shadow-elevated dark:shadow-elevated-dark',
          'animate-scale-in border border-gray-200 dark:border-gray-700',
          'overflow-hidden'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Input de Busca */}
        <div className="flex items-center gap-3 p-4 border-b border-gray-200 dark:border-gray-700">
          <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Buscar notas, clientes, produtos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 bg-transparent text-lg text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-all duration-200 ease-out"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Resultados */}
        <div className="max-h-96 overflow-y-auto custom-scrollbar">
          {loading && (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <p className="text-sm">Buscando...</p>
            </div>
          )}

          {!loading && searchTerm.length >= 2 && results.length === 0 && (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <p className="text-sm">Nenhum resultado encontrado para "{searchTerm}"</p>
            </div>
          )}

          {!loading && results.length > 0 && (
            <div ref={resultsRef} className="p-2">
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700 mb-2">
                {results.length} resultado{results.length !== 1 ? 's' : ''} encontrado{results.length !== 1 ? 's' : ''}
              </div>
              {results.map((result, index) => (
                <button
                  key={`${result.type}-${result.id}`}
                  onClick={() => handleResultClick(result)}
                  className={cn(
                    'w-full px-4 py-3 rounded-lg text-left transition-all duration-200 ease-out mb-1 last:mb-0',
                    'flex items-start gap-3',
                    index === selectedIndex
                      ? 'bg-primary-100/30 dark:bg-primary-900/15 text-primary-700 dark:text-primary-300'
                      : 'hover:bg-gray-100/60 dark:hover:bg-gray-700/50 text-gray-900 dark:text-gray-100'
                  )}
                >
                  <div className={cn(
                    'mt-0.5 flex-shrink-0',
                    index === selectedIndex ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'
                  )}>
                    {getIcon(result.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-sm truncate">
                        {result.title}
                      </p>
                      <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                        {getTypeLabel(result.type)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                      {result.subtitle}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {!loading && searchTerm.length < 2 && (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <p className="text-sm">Digite pelo menos 2 caracteres para buscar</p>
              <div className="mt-4 flex items-center justify-center gap-4 text-xs">
                <div className="flex items-center gap-1">
                  <ArrowUp className="w-3 h-3" />
                  <ArrowDown className="w-3 h-3" />
                  <span>Navegar</span>
                </div>
                <div className="flex items-center gap-1">
                  <kbd className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">Enter</kbd>
                  <span>Selecionar</span>
                </div>
                <div className="flex items-center gap-1">
                  <kbd className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">Esc</kbd>
                  <span>Fechar</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

